import { Component, OnDestroy, OnInit } from '@angular/core';

import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

import { PartAddInventoryDialogComponent } from 'app/modules/app-components/part-add-inventory-dialog/part-add-inventory-dialog.component';

import { systemSpecificTags } from 'app/constants';
import { DataService, HelperService, HTTPService } from 'app/services';

import { Subscription } from 'rxjs';

@Component({
  selector: 'ngx-catalogue-dialog',
  templateUrl: './catalogue-dialog.component.html',
  styleUrls: ['./catalogue-dialog.component.scss'],
  providers: [DialogService],
})
export class CatalogueDialogComponent implements OnInit, OnDestroy {
  isSearching: boolean = false;
  itemResults: any[] = [];
  onlineItem: any = {};
  orderId: string = '';
  estimate: any;
  associatedItems: any = {};
  searchTypeIcon = {
    part: 'fal fa-tools',
    labor: 'far fa-user-hard-hat',
    tire: 'fal fas fa-tire',
    hazmatFee: 'far fa-usd-circle',
    tireStorage: 'fal fa-inventory',
  };
  inventoryItems: string[] = ['part', 'tire'];
  dataSubsription: Subscription;
  catalogDialogType: string = 'supplier';
  headerIcon: string = 'fal fa-warehouse';
  placeholder: string = 'Pick an item from catalogue';
  saveBtnLabel: string = 'Confirm';
  cancelBtnLabel: string = 'Cancel';

  constructor(
    private dataService: DataService,
    private helperService: HelperService,
    private httpService: HTTPService,
    private dynamicDialogRef: DynamicDialogRef,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dialogService: DialogService
  ) { }

  async ngOnInit() {
    let { estimate, orderId, item, openFrom } = this.dynamicDialogConfig.data;

    if (['workboard', 'partsOrdering'].includes(openFrom)) {
      try {
        const order = await this.httpService.getEstimateById(orderId, false, false);
        const _source = item.itemSource === 'Manual Order' ? 'ManualOrder' : item.itemSource;
        const _item = this.helperService.getPartsOrderingItem(order, _source, item.orderItemId);

        if (item.category) {
          //for standardization of tire type and category
          _item.category = item.category;
        }

        if (_item && !_item?.code) {
          _item.code = item.code;
        }

        if (_item) {
          item = {
            ...item, // need some item mapping from parts ordring (QBO id and Pricing matrix)
            ..._item,
            coreCost: item.coreCost || _item?.coreCost,
            isOpenFromInventory: true,
            isPopulateDynamically: true,
          };

          if (['PartsTech', 'Nexpart', 'TireHub', 'TireConnect', 'RepairLink', 'SpeedDial'].includes(item.itemSource)) {
            item.isPopulateDynamically = false;
          } else {
            item.orderItemId = _item._id;
          }

          if(!item.itemId) {
            item.itemId = 'no-id';
          }
        }
      } catch (err) {
        console.log(err);
      }
    }

    this.estimate = estimate;
    this.onlineItem = item;
    this.orderId = orderId;

    if (this.onlineItem) {
      if (this.onlineItem.title) {
        this.associatedItems.title = this.onlineItem.title;
        this.handleSearchSuggestions(this.associatedItems.title);
      }

      if (this.onlineItem.isOpenFromInventory) {
        this.catalogDialogType = 'inventory';
        this.headerIcon = 'far fa-boxes';
        this.placeholder = this.onlineItem.type === 'part' ? 'Select Part' : 'Select Tire';
        this.saveBtnLabel = 'Add to inventory';
        this.cancelBtnLabel = 'Save as new';
      }
    }

    this.dataSubsription = this.dataService.getData().subscribe((data) => {
      if (data) {
        if (data.data.type === 'passdata') {
          if (data.data.key === 'catalogue-item' && data.data && data.data.data) {
            this.associatedItems = data.data && data.data.data;
          } else if (data.data.key === 'item-inventory-add' && data.data) {
            this.associatedItems = data.data && data.data.data;

            this.handleSubmit();
          }
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.dataSubsription) {
      this.dataSubsription.unsubscribe();
    }
  }

  handleSearchSuggestions(query: string = '') {
    this.isSearching = true;

    this.httpService
      .getItems({ limit: 100, search: query.trim(), type: this.onlineItem.type })
      .then(
        (data: any) => {
          this.itemResults = [];

          if (data.records) {
            this.itemResults = data.records;
            // console.log('testing', this.itemResults)
          }
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      )
      .finally(() => {
        this.isSearching = false;
      });
  }

  handleSelect(item: any) {
    this.associatedItems = item;
  }

  handleClear() {
    this.itemResults = [];
  }

  handleCancel() {
    this.dynamicDialogRef.close({ isSuccess: false });
  }

  handleClose() {
    if (this.catalogDialogType === 'inventory') {
      this.handleOpenItemModal('saveAsNew');
    } else {
      this.dynamicDialogRef.close({ isSuccess: false });
    }
  }

  handleSubmit() {
    if (this.catalogDialogType === 'supplier') {
      this.handleUpdateSupplier();
    } else {
      this.handleUpdateInventory();
    }
  }

  handleUpdateSupplier() {
    if (!this.associatedItems._id) return;

    this.associatedItems.price = this.onlineItem.price;
    this.associatedItems.cost = this.onlineItem.cost;
    this.associatedItems.count = this.onlineItem.count;
    this.associatedItems.pricing = this.onlineItem.pricing;
    this.associatedItems.notes = this.onlineItem.notes;
    this.associatedItems.itemSource = '';

    if (this.onlineItem.tags && this.onlineItem.tags.length) {
      this.associatedItems.tags = this.onlineItem.tags.filter(({ name }) => !systemSpecificTags.includes(name));
    }

    this.dynamicDialogRef.close({ isSuccess: true, data: this.associatedItems, onlineItem: this.onlineItem });
  }

  handleUpdateInventory() {
    if (!this.associatedItems._id) return;

    this.associatedItems.count = this.onlineItem.count;
    this.associatedItems.cost = this.onlineItem.cost;

    if (!this.associatedItems.stocked && !(this.associatedItems.locations && this.associatedItems.locations.length)) {
      this.handleOpenItemModal();
      return;
    }

    else if (this.associatedItems.stocked && !this.associatedItems.locations[0].locationId) {
      this.handleOpenItemModal();
      return;
    }

    this.dynamicDialogRef.close({ isSuccess: false });
    const ref = this.dialogService.open(PartAddInventoryDialogComponent, {
      width: '970px',
      styleClass: `add-dialog add-to-inventory-dialog`,
      showHeader: false,
      data: {
        dialogType: 'inventory',
        estimate: this.estimate,
        orderId: this.orderId,
        actualItem: this.onlineItem,
        associatedItem: this.associatedItems,
      },
    });
    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        this.dataService.sendData(this.dataService.typePassData('refresh-estimate', {}));
      }
      onClose.unsubscribe();
    });
  }

  openPartModal(itemId: string = '') {
    this.dataService.sendData(
      this.dataService.typePassData('open-item-dialog', {
        type: 'part',
        isOpenFromRO: false,
        payload: itemId,
      })
    );
  }

  highlighedItemType(text: string, query: string) {
    if (text && query) {
      let str = text.replace(/\s/g, '&nbsp;');

      const regex = new RegExp(`(${query.replace(/\s/g, '&nbsp;')})`, 'ig');
      str = str.replace(regex, `<span class="font-600"><b>$1</b></span>`);

      return str;
    }

    return text;
  }

  handleOpenItemModal(dialogType: string = '') {
    const data = {
      onlineItem: null,
      isOpenFromRO: false,
      type: this.onlineItem.type,
      payload: { isOpenFromInventoryAdd: false, itemId: '' },
    };

    if (this.catalogDialogType === 'inventory') {
      data.payload.isOpenFromInventoryAdd = true;
    }

    if (dialogType === 'create') {
      if (this.onlineItem && this.onlineItem.itemId) {
        data.onlineItem = this.helperService.deepCopy(this.onlineItem);
        data.onlineItem.isPreFilledPartFields = true;

        if (!data.payload.isOpenFromInventoryAdd) {
          delete data.onlineItem.itemSource;
        }

        delete data.onlineItem._id;
        delete data.onlineItem.itemId;
      } else {
        data.onlineItem = this.onlineItem || null;
        data.onlineItem.isPreFilledPartFields = true;
      }
    } else if (dialogType === 'saveAsNew') {
      data.onlineItem = this.helperService.deepCopy(this.onlineItem);
      if (!this.dataService.isNewTireModalEnabled) {
        data.onlineItem.title += '(Inventory)';
      }
      data.onlineItem.isPreFilledPartFields = true;

      if (this.associatedItems && this.associatedItems._id) {
        data.payload.itemId = this.associatedItems._id;
      }

      delete data.onlineItem._id;
      delete data.onlineItem.itemId;
    } else if (this.associatedItems && this.associatedItems._id) {
      data.payload.itemId = this.associatedItems._id;
    }

    this.dataService.sendData(this.dataService.typePassData('open-item-dialog', data));
  }
}
